import { combineReducers } from 'redux';
import {
  ICurrentOrganisation,
  IOrganisationUser,
  IOrganisationUserInvitation,
} from '@liquid-state/pephealth-client';
import { LOGIN_REQUIRED, LOGOUT_SUCCESSFUL, SESSION_ESTABLISHED } from '@redux/login/actions';
import { DirectChildOrganisation } from './client';
import {
  DirectChildOrganisationAdministrationAction,
  CREATE_DIRECT_CHILD_ORGANISATION,
  CREATE_DIRECT_CHILD_ORGANISATION_FAILURE,
  CREATE_DIRECT_CHILD_ORGANISATION_SUCCESS,
  EDIT_DIRECT_CHILD_ORGANISATION_USER,
  EDIT_DIRECT_CHILD_ORGANISATION_USER_FAILURE,
  EDIT_DIRECT_CHILD_ORGANISATION_USER_SUCCESS,
  LOAD_DIRECT_CHILD_ORGANISATIONS,
  LOAD_DIRECT_CHILD_ORGANISATIONS_FAILURE,
  LOAD_DIRECT_CHILD_ORGANISATIONS_SUCCESS,
  LOAD_DIRECT_CHILD_ORGANISATION,
  LOAD_DIRECT_CHILD_ORGANISATION_FAILURE,
  LOAD_DIRECT_CHILD_ORGANISATION_SUCCESS,
  LOAD_CURRENT_ORGANISATION,
  LOAD_CURRENT_ORGANISATION_FAILURE,
  LOAD_CURRENT_ORGANISATION_SUCCESS,
  LOAD_DIRECT_CHILD_ORGANISATION_USERS,
  LOAD_DIRECT_CHILD_ORGANISATION_USERS_FAILURE,
  LOAD_DIRECT_CHILD_ORGANISATION_USERS_SUCCESS,
  INVITE_DIRECT_CHILD_ORGANISATION_USER,
  INVITE_DIRECT_CHILD_ORGANISATION_USER_FAILURE,
  INVITE_DIRECT_CHILD_ORGANISATION_USER_SUCCESS,
  LOAD_DIRECT_CHILD_ORGANISATION_USER_DETAILS,
  LOAD_DIRECT_CHILD_ORGANISATION_USER_DETAILS_FAILURE,
  LOAD_DIRECT_CHILD_ORGANISATION_USER_DETAILS_SUCCESS,
  RESET_DIRECT_CHILD_ORGANISATION_USER_INVITATION,
  RESET_DIRECT_CHILD_ORGANISATION_USER_EDIT,
  RESET_DIRECT_CHILD_ORGANISATION_CREATION,
  RESEND_DIRECT_CHILD_ORGANISATION_USER_INVITATION,
  RESEND_DIRECT_CHILD_ORGANISATION_USER_INVITATION_FAILURE,
  RESEND_DIRECT_CHILD_ORGANISATION_USER_INVITATION_SUCCESS,
  UPDATE_DIRECT_CHILD_ORGANISATION_USER_PROFILE_PICTURE,
} from './actions';

export type RequestStatus = 'idle' | 'loading' | 'success' | 'failure';

export interface CurrentOrganisationState {
  status: RequestStatus;
  data: ICurrentOrganisation | null;
  error: string | null;
}

const initialState: CurrentOrganisationState = {
  status: 'idle',
  data: null,
  error: null,
};

const currentOrganisation = (
  state = initialState,
  action: DirectChildOrganisationAdministrationAction | { type: string },
): CurrentOrganisationState => {
  switch (action.type) {
    case SESSION_ESTABLISHED:
    case LOAD_CURRENT_ORGANISATION:
      return { status: 'loading', data: null, error: null };
    case LOAD_CURRENT_ORGANISATION_SUCCESS:
      return {
        status: 'success',
        data: (action as ReturnType<typeof import('./actions').loadCurrentOrganisationSuccess>)
          .payload.organisation,
        error: null,
      };
    case LOAD_CURRENT_ORGANISATION_FAILURE:
      return {
        status: 'failure',
        data: null,
        error: (action as ReturnType<typeof import('./actions').loadCurrentOrganisationFailure>)
          .payload.error,
      };
    case LOGIN_REQUIRED:
    case LOGOUT_SUCCESSFUL:
      return initialState;
    default:
      return state;
  }
};

export interface DirectChildOrganisationsState {
  status: RequestStatus;
  bySlug: Record<string, DirectChildOrganisation>;
  slugs: string[];
  error: string | null;
  errorStatus?: number;
  errorCode?: string;
  details: Record<
    string,
    {
      status: RequestStatus;
      error: string | null;
      errorStatus?: number;
    }
  >;
}

const initialDirectChildOrganisationsState: DirectChildOrganisationsState = {
  status: 'idle',
  bySlug: {},
  slugs: [],
  error: null,
  details: {},
};

const directChildOrganisations = (
  state = initialDirectChildOrganisationsState,
  action: DirectChildOrganisationAdministrationAction | { type: string },
): DirectChildOrganisationsState => {
  switch (action.type) {
    case LOAD_DIRECT_CHILD_ORGANISATIONS:
      return {
        ...state,
        status: 'loading',
        slugs: [],
        error: null,
        errorStatus: undefined,
        errorCode: undefined,
      };
    case LOAD_DIRECT_CHILD_ORGANISATIONS_SUCCESS: {
      const organisations = (action as ReturnType<
        typeof import('./actions').loadDirectChildOrganisationsSuccess
      >).payload.organisations;

      return {
        status: 'success',
        bySlug: organisations.reduce(
          (bySlug, organisation) => ({ ...bySlug, [organisation.slug]: organisation }),
          {},
        ),
        slugs: organisations.map(organisation => organisation.slug),
        error: null,
        errorStatus: undefined,
        errorCode: undefined,
        details: state.details,
      };
    }
    case LOAD_DIRECT_CHILD_ORGANISATIONS_FAILURE: {
      const { error, status, errorCode } = (action as ReturnType<
        typeof import('./actions').loadDirectChildOrganisationsFailure
      >).payload;

      return {
        ...state,
        status: 'failure',
        slugs: [],
        error,
        errorStatus: status,
        errorCode,
      };
    }
    case LOAD_DIRECT_CHILD_ORGANISATION: {
      const organisationSlug = (action as ReturnType<
        typeof import('./actions').loadDirectChildOrganisation
      >).payload.organisationSlug;
      const bySlug = { ...state.bySlug };
      delete bySlug[organisationSlug];

      return {
        ...state,
        bySlug,
        details: {
          ...state.details,
          [organisationSlug]: { status: 'loading', error: null, errorStatus: undefined },
        },
      };
    }
    case LOAD_DIRECT_CHILD_ORGANISATION_SUCCESS: {
      const organisation = (action as ReturnType<
        typeof import('./actions').loadDirectChildOrganisationSuccess
      >).payload.organisation;

      return {
        ...state,
        bySlug: { ...state.bySlug, [organisation.slug]: organisation },
        details: {
          ...state.details,
          [organisation.slug]: { status: 'success', error: null, errorStatus: undefined },
        },
      };
    }
    case LOAD_DIRECT_CHILD_ORGANISATION_USER_DETAILS_SUCCESS: {
      const { organisation } = (action as ReturnType<
        typeof import('./actions').loadDirectChildOrganisationUserDetailsSuccess
      >).payload;

      return {
        ...state,
        bySlug: { ...state.bySlug, [organisation.slug]: organisation },
        details: {
          ...state.details,
          [organisation.slug]: { status: 'success', error: null, errorStatus: undefined },
        },
      };
    }
    case LOAD_DIRECT_CHILD_ORGANISATION_FAILURE: {
      const { organisationSlug, error, status } = (action as ReturnType<
        typeof import('./actions').loadDirectChildOrganisationFailure
      >).payload;
      const bySlug = { ...state.bySlug };
      delete bySlug[organisationSlug];

      return {
        ...state,
        bySlug,
        details: {
          ...state.details,
          [organisationSlug]: { status: 'failure', error, errorStatus: status },
        },
      };
    }
    case LOGIN_REQUIRED:
    case LOGOUT_SUCCESSFUL:
      return initialDirectChildOrganisationsState;
    default:
      return state;
  }
};

export interface DirectChildOrganisationCreationState {
  status: RequestStatus;
}

const initialDirectChildOrganisationCreationState: DirectChildOrganisationCreationState = {
  status: 'idle',
};

const directChildOrganisationCreation = (
  state = initialDirectChildOrganisationCreationState,
  action: DirectChildOrganisationAdministrationAction | { type: string },
): DirectChildOrganisationCreationState => {
  switch (action.type) {
    case CREATE_DIRECT_CHILD_ORGANISATION:
      return { status: 'loading' };
    case CREATE_DIRECT_CHILD_ORGANISATION_SUCCESS:
      return { status: 'success' };
    case CREATE_DIRECT_CHILD_ORGANISATION_FAILURE:
      return { status: 'failure' };
    case RESET_DIRECT_CHILD_ORGANISATION_CREATION:
    case LOGIN_REQUIRED:
    case LOGOUT_SUCCESSFUL:
      return initialDirectChildOrganisationCreationState;
    default:
      return state;
  }
};

export interface DirectChildOrganisationUser extends IOrganisationUser {
  organisationSlug: string;
  organisationName: string;
}

export interface DirectChildOrganisationUsersState {
  status: RequestStatus;
  byOrganisation: Record<string, Record<string, DirectChildOrganisationUser>>;
  organisationSlugs: string[];
  error: string | null;
  errorStatus?: number;
}

const initialDirectChildOrganisationUsersState: DirectChildOrganisationUsersState = {
  status: 'idle',
  byOrganisation: {},
  organisationSlugs: [],
  error: null,
};

const directChildOrganisationUsers = (
  state = initialDirectChildOrganisationUsersState,
  action: DirectChildOrganisationAdministrationAction | { type: string },
): DirectChildOrganisationUsersState => {
  switch (action.type) {
    case LOAD_DIRECT_CHILD_ORGANISATION_USERS:
      return {
        status: 'loading',
        byOrganisation: {},
        organisationSlugs: [],
        error: null,
      };
    case LOAD_DIRECT_CHILD_ORGANISATION_USERS_SUCCESS: {
      const { results } = (action as ReturnType<
        typeof import('./actions').loadDirectChildOrganisationUsersSuccess
      >).payload;

      return {
        status: 'success',
        byOrganisation: results.reduce(
          (byOrganisation, { organisation, users }) => ({
            ...byOrganisation,
            [organisation.slug]: users
              .filter(user => user.role === 'admin' || user.role === 'member')
              .reduce(
                (byId, user) => ({
                  ...byId,
                  [String(user.id)]: {
                    ...user,
                    organisation: organisation.slug,
                    organisationSlug: organisation.slug,
                    organisationName: organisation.name,
                  },
                }),
                {},
              ),
          }),
          {},
        ),
        organisationSlugs: results.map(({ organisation }) => organisation.slug),
        error: null,
      };
    }
    case LOAD_DIRECT_CHILD_ORGANISATION_USER_DETAILS: {
      const { organisationSlug, userId } = (action as ReturnType<
        typeof import('./actions').loadDirectChildOrganisationUserDetails
      >).payload;
      const usersForOrganisation = { ...(state.byOrganisation[organisationSlug] || {}) };
      delete usersForOrganisation[String(userId)];

      return {
        ...state,
        byOrganisation: {
          ...state.byOrganisation,
          [organisationSlug]: usersForOrganisation,
        },
      };
    }
    case LOAD_DIRECT_CHILD_ORGANISATION_USER_DETAILS_SUCCESS: {
      const { organisation, user } = (action as ReturnType<
        typeof import('./actions').loadDirectChildOrganisationUserDetailsSuccess
      >).payload;

      return {
        ...state,
        byOrganisation: {
          ...state.byOrganisation,
          [organisation.slug]: {
            ...(state.byOrganisation[organisation.slug] || {}),
            [String(user.id)]: {
              ...user,
              organisation: organisation.slug,
              organisationSlug: organisation.slug,
              organisationName: organisation.name,
            },
          },
        },
      };
    }
    case LOAD_DIRECT_CHILD_ORGANISATION_USERS_FAILURE: {
      const { error, status } = (action as ReturnType<
        typeof import('./actions').loadDirectChildOrganisationUsersFailure
      >).payload;

      return {
        status: 'failure',
        byOrganisation: {},
        organisationSlugs: [],
        error,
        errorStatus: status,
      };
    }
    case UPDATE_DIRECT_CHILD_ORGANISATION_USER_PROFILE_PICTURE: {
      const { userId, picture } = (action as ReturnType<
        typeof import('./actions').updateDirectChildOrganisationUserProfilePicture
      >).payload;
      const key = String(userId);
      let changed = false;
      const byOrganisation = Object.keys(state.byOrganisation).reduce(
        (nextByOrganisation, organisationSlug) => {
          const existing = state.byOrganisation[organisationSlug]?.[key];
          if (!existing) {
            return {
              ...nextByOrganisation,
              [organisationSlug]: state.byOrganisation[organisationSlug],
            };
          }

          changed = true;
          return {
            ...nextByOrganisation,
            [organisationSlug]: {
              ...state.byOrganisation[organisationSlug],
              [key]: {
                ...existing,
                profilePictureImageId: picture.profilePictureImageId,
                profilePictureUrl: picture.profilePictureUrl,
              },
            },
          };
        },
        {} as DirectChildOrganisationUsersState['byOrganisation'],
      );

      if (!changed) return state;

      return {
        ...state,
        byOrganisation,
      };
    }
    case LOGIN_REQUIRED:
    case LOGOUT_SUCCESSFUL:
      return initialDirectChildOrganisationUsersState;
    default:
      return state;
  }
};

export interface DirectChildOrganisationUserInvitationState {
  status: RequestStatus;
}

const initialDirectChildOrganisationUserInvitationState: DirectChildOrganisationUserInvitationState = {
  status: 'idle',
};

const directChildOrganisationUserInvitation = (
  state = initialDirectChildOrganisationUserInvitationState,
  action: DirectChildOrganisationAdministrationAction | { type: string },
): DirectChildOrganisationUserInvitationState => {
  switch (action.type) {
    case INVITE_DIRECT_CHILD_ORGANISATION_USER:
      return { status: 'loading' };
    case INVITE_DIRECT_CHILD_ORGANISATION_USER_SUCCESS:
      return { status: 'success' };
    case INVITE_DIRECT_CHILD_ORGANISATION_USER_FAILURE:
      return { status: 'failure' };
    case RESET_DIRECT_CHILD_ORGANISATION_USER_INVITATION:
    case LOGIN_REQUIRED:
    case LOGOUT_SUCCESSFUL:
      return initialDirectChildOrganisationUserInvitationState;
    default:
      return state;
  }
};

export interface DirectChildOrganisationUserEditState {
  requests: Record<string, Record<string, { status: RequestStatus }>>;
}

const initialDirectChildOrganisationUserEditState: DirectChildOrganisationUserEditState = {
  requests: {},
};

const directChildOrganisationUserEdit = (
  state = initialDirectChildOrganisationUserEditState,
  action: DirectChildOrganisationAdministrationAction | { type: string },
): DirectChildOrganisationUserEditState => {
  switch (action.type) {
    case EDIT_DIRECT_CHILD_ORGANISATION_USER:
    case EDIT_DIRECT_CHILD_ORGANISATION_USER_SUCCESS:
    case EDIT_DIRECT_CHILD_ORGANISATION_USER_FAILURE:
    case RESET_DIRECT_CHILD_ORGANISATION_USER_EDIT: {
      const { organisationSlug, userId } = (action as ReturnType<
        typeof import('./actions').editDirectChildOrganisationUser
      >).payload;
      const organisationRequests = { ...(state.requests[organisationSlug] || {}) };

      if (action.type === RESET_DIRECT_CHILD_ORGANISATION_USER_EDIT) {
        delete organisationRequests[String(userId)];
      } else {
        organisationRequests[String(userId)] = {
          status:
            action.type === EDIT_DIRECT_CHILD_ORGANISATION_USER
              ? 'loading'
              : action.type === EDIT_DIRECT_CHILD_ORGANISATION_USER_SUCCESS
              ? 'success'
              : 'failure',
        };
      }

      return {
        requests: {
          ...state.requests,
          [organisationSlug]: organisationRequests,
        },
      };
    }
    case LOGIN_REQUIRED:
    case LOGOUT_SUCCESSFUL:
      return initialDirectChildOrganisationUserEditState;
    default:
      return state;
  }
};

interface DirectChildOrganisationUserDetailsRequest {
  status: RequestStatus;
  error: string | null;
  errorStatus?: number;
}

interface DirectChildOrganisationUserInvitationsCollection {
  status: RequestStatus;
  items: IOrganisationUserInvitation[];
  error: string | null;
  errorStatus?: number;
  resendStatus: RequestStatus;
}

export interface DirectChildOrganisationUserDetailsState {
  requests: Record<string, Record<string, DirectChildOrganisationUserDetailsRequest>>;
  invitations: Record<string, Record<string, DirectChildOrganisationUserInvitationsCollection>>;
}

const initialDirectChildOrganisationUserDetailsState: DirectChildOrganisationUserDetailsState = {
  requests: {},
  invitations: {},
};

const directChildOrganisationUserDetails = (
  state = initialDirectChildOrganisationUserDetailsState,
  action: DirectChildOrganisationAdministrationAction | { type: string },
): DirectChildOrganisationUserDetailsState => {
  switch (action.type) {
    case LOAD_DIRECT_CHILD_ORGANISATION_USER_DETAILS: {
      const { organisationSlug, userId } = (action as ReturnType<
        typeof import('./actions').loadDirectChildOrganisationUserDetails
      >).payload;

      return {
        requests: {
          ...state.requests,
          [organisationSlug]: {
            ...(state.requests[organisationSlug] || {}),
            [String(userId)]: { status: 'loading', error: null },
          },
        },
        invitations: {
          ...state.invitations,
          [organisationSlug]: {
            ...(state.invitations[organisationSlug] || {}),
            [String(userId)]: {
              status: 'loading',
              items: [],
              error: null,
              resendStatus: 'idle',
            },
          },
        },
      };
    }
    case LOAD_DIRECT_CHILD_ORGANISATION_USER_DETAILS_SUCCESS: {
      const { organisation, user, invitations } = (action as ReturnType<
        typeof import('./actions').loadDirectChildOrganisationUserDetailsSuccess
      >).payload;
      const userId = String(user.id);

      return {
        requests: {
          ...state.requests,
          [organisation.slug]: {
            ...(state.requests[organisation.slug] || {}),
            [userId]: { status: 'success', error: null },
          },
        },
        invitations: {
          ...state.invitations,
          [organisation.slug]: {
            ...(state.invitations[organisation.slug] || {}),
            [userId]: {
              status: 'success',
              items: invitations.map(({ id, status, expires, sentBy, created }) => ({
                id,
                status,
                expires,
                sentBy,
                created,
              })),
              error: null,
              resendStatus: state.invitations[organisation.slug]?.[userId]?.resendStatus || 'idle',
            },
          },
        },
      };
    }
    case LOAD_DIRECT_CHILD_ORGANISATION_USER_DETAILS_FAILURE: {
      const { organisationSlug, userId, error, status } = (action as ReturnType<
        typeof import('./actions').loadDirectChildOrganisationUserDetailsFailure
      >).payload;

      return {
        requests: {
          ...state.requests,
          [organisationSlug]: {
            ...(state.requests[organisationSlug] || {}),
            [String(userId)]: { status: 'failure', error, errorStatus: status },
          },
        },
        invitations: {
          ...state.invitations,
          [organisationSlug]: {
            ...(state.invitations[organisationSlug] || {}),
            [String(userId)]: {
              status: 'failure',
              items: [],
              error,
              errorStatus: status,
              resendStatus: 'idle',
            },
          },
        },
      };
    }
    case RESEND_DIRECT_CHILD_ORGANISATION_USER_INVITATION:
    case RESEND_DIRECT_CHILD_ORGANISATION_USER_INVITATION_SUCCESS:
    case RESEND_DIRECT_CHILD_ORGANISATION_USER_INVITATION_FAILURE: {
      const { organisationSlug, userId } = (action as ReturnType<
        typeof import('./actions').resendDirectChildOrganisationUserInvitation
      >).payload;
      const existing = state.invitations[organisationSlug]?.[String(userId)] || {
        status: 'idle',
        items: [],
        error: null,
        resendStatus: 'idle',
      };
      const resendStatus =
        action.type === RESEND_DIRECT_CHILD_ORGANISATION_USER_INVITATION
          ? 'loading'
          : action.type === RESEND_DIRECT_CHILD_ORGANISATION_USER_INVITATION_SUCCESS
          ? 'success'
          : 'failure';

      return {
        ...state,
        invitations: {
          ...state.invitations,
          [organisationSlug]: {
            ...(state.invitations[organisationSlug] || {}),
            [String(userId)]: { ...existing, resendStatus },
          },
        },
      };
    }
    case LOGIN_REQUIRED:
    case LOGOUT_SUCCESSFUL:
      return initialDirectChildOrganisationUserDetailsState;
    default:
      return state;
  }
};

export default combineReducers({
  currentOrganisation,
  directChildOrganisations,
  directChildOrganisationCreation,
  directChildOrganisationUsers,
  directChildOrganisationUserInvitation,
  directChildOrganisationUserEdit,
  directChildOrganisationUserDetails,
});
